import { Router } from '@angular/router';
import { Component, OnInit, ViewEncapsulation, Input } from '@angular/core';

import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import values from 'lodash/values';

import { UserManager, USER_ACCOUNT_TYPE, AUTH_FORMS } from '@cakeiteasy/order-process';

import { CURRENT_LANG_APP_ROUTES } from '@entire/route-names';
import { LocationManagerService } from '@cakeiteasy/web-utils';
import { AnalyticTracker } from '@analytic/services/analytic-tracker.service';

@Component({
  standalone: false,
  templateUrl: './user-auth-modal.component.html',
  styleUrls: ['./user-auth-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UserAuthModalComponent implements OnInit {
  @Input() showedForm: string = '';
  @Input() hideLoginFormFooter = false;
  @Input() resetRequestSuccess = false;
  @Input() resetPasswordRequired = false;

  public countryCode: string;
  public busy: Promise<any>;
  public error: string;
  public accountType = USER_ACCOUNT_TYPE.GENERAL;

  public AUTH_FORMS = AUTH_FORMS;
  public USER_ACCOUNT_TYPE = USER_ACCOUNT_TYPE;

  constructor(public modal: NgbActiveModal,
              private _router: Router,
              private _toastr: ToastrService,
              private _users: UserManager,
              private _translate: TranslateService,
              private _locationManager: LocationManagerService,
              private _analyticTracker: AnalyticTracker) {
  }

  ngOnInit() {
    if (!AUTH_FORMS[this.showedForm]) {
      this.showedForm = this.AUTH_FORMS.LOGIN;
    }

    this._initAccountType();

    this.countryCode = this._locationManager.getDomainName().toUpperCase();
  }

  public login(credentials) {
    this._users.login(credentials)
      .then((resp) => {
        this.trackLogin(resp);

        this.modal.close();
      })
      .catch((e) => {
        this.resetPasswordRequired = e.error && e.error.resetPasswordRequired;

        if (!this.resetPasswordRequired) {
          if (e.status === 401) {
            this.error = this._translate.instant('modal.login.passwordIsIncorrect');
          } else if (e.status === 404) {
            this.error = this._translate.instant('modal.login.emailIsNotRegistered');
          } else {
            this.error = this._translate.instant('modal.login.error');
          }
        }
      });
  }

  public register(user) {
    this.busy = this._users.register(user)
      .then((resp) => {
        this.trackSignUp(resp);

        this.modal.close();
        this._toastr.success(
          this._translate.instant('notifications.auth.register.message'),
        );
      })
      .catch((err) => {
        if (err.status === 400) {
          this.error = this._translate.instant('modal.register.error.emailIsBusy');
        } else {
          this.error = this._translate.instant('modal.register.error.unexpected');
        }
      });
  }

  public resetRequest(email) {
    this._users.restorePasswordRequest({ email, pagePath: 'change-password' })
      .then(() => {
        // if restore password after migrate
        this.showedForm = AUTH_FORMS.LOST_PASSWORD;

        this.resetRequestSuccess = true;
        setTimeout(() => this.modal.close(), 3000);
      })
      .catch((err) => {
        this.error = this._translate.instant('modal.lostPassword.error');
      });
  }

  private trackLogin(payload) {
    return this._analyticTracker.trackLogin(this.mapToAuthPayload(payload));
  }

  private trackSignUp(payload) {
    return this._analyticTracker.trackSignUp(this.mapToAuthPayload(payload));
  }

  private mapToAuthPayload(data) {
    const { id: userId, companyName, email, phone } = data;
    return {
      userId, email, phone,
      method: 'email',
      isB2B: !!companyName,
    };
  }

  private _initAccountType() {
    const isBusinessPage = [...values(CURRENT_LANG_APP_ROUTES.B2B)]
      .some(route => this._router.url.indexOf(route) >= 0);

    if (this.showedForm === AUTH_FORMS.REGISTER_BUSINESS
      || this.showedForm === AUTH_FORMS.REGISTER && isBusinessPage) {
      this.accountType = USER_ACCOUNT_TYPE.SHARED_BUSINESS_ACCOUNT;
    }
  }
}

